
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type StatusType = 'PENDING' | 'APPROVED' | 'REJECTED' | string;

type ReportStatusBadgeProps = {
  status: StatusType;
  rejectionReason?: string;
};

export const ReportStatusBadge: React.FC<ReportStatusBadgeProps> = ({ 
  status,
  rejectionReason
}) => {
  const { t } = useTranslation();
  const getStatusConfig = (status: StatusType) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED':
        return { variant: 'success' as const, label: t('reports.status.approved') };
      case 'REJECTED':
        return { variant: 'destructive' as const, label: t('reports.status.rejected') };
      case 'PENDING':
      default:
        return { variant: 'outline' as const, label: t('reports.status.pending') };
    }
  };

  const { variant, label } = getStatusConfig(status);

  // Show tooltip only if status is rejected and there's a rejection reason
  const isRejected = status?.toUpperCase() === 'REJECTED';
  
  if (isRejected && rejectionReason) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Badge variant={variant}>{label}</Badge>
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-white text-black border border-gray-200 p-2 max-w-xs">
            <p>{rejectionReason}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return <Badge variant={variant}>{label}</Badge>;
};
